import { useState, useEffect } from "react";
import { useScanLogs, useScanSources, useCommander } from "@/hooks/use-mock-data";
import type { ScanSource, ScanLog, ScanSourceDetail } from "@/hooks/use-mock-data";
// Source config editor: click source name to open config dialog
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ToggleLeft,
  ToggleRight,
  Eye,
  EyeOff,
  Radar,
  Hammer,
  TrendingUp,
  Lock,
  KeyRound,
  Flame,
  Database,
  RefreshCw,
  Archive,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  X,
  Plus,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";

interface AiUsageStats {
  today: { totalTokens: number; estimatedCost: number };
  thisWeek: { totalTokens: number; estimatedCost: number };
  thisMonth: { totalTokens: number; estimatedCost: number };
  budgetCeiling: number;
}

const LAYER_CONFIG: Record<string, { label: string; icon: typeof Radar; color: string }> = {
  pain_scanner: { label: "PAIN SCANNER", icon: Radar, color: "text-red-400" },
  builder_intel: { label: "BUILDER INTEL", icon: Hammer, color: "text-electric-blue" },
  trend_signal: { label: "TREND SIGNALS", icon: TrendingUp, color: "text-mint" },
};

export default function SettingsPage() {
  const { data: commander } = useCommander();
  const { data: scanLogs } = useScanLogs();
  const { data: scanSources, isLoading: sourcesLoading, refetch: refetchSources } = useScanSources();
  const [showApiKey, setShowApiKey] = useState(false);
  const [aiUsage, setAiUsage] = useState<AiUsageStats | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [markingLegacy, setMarkingLegacy] = useState(false);
  const [clearingSignals, setClearingSignals] = useState(false);

  useEffect(() => {
    api
      .get<AiUsageStats>("/api/stats/ai-usage")
      .then(setAiUsage)
      .catch(() => {});
  }, []);

  const [editingSource, setEditingSource] = useState<ScanSource | null>(null);

  const toggleSource = async (source: ScanSource) => {
    try {
      await api.patch(`/api/scan-sources/${source.id}`, { enabled: !source.enabled });
      refetchSources();
    } catch {
      toast.error("Failed to update source");
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post<{ message: string; count: number }>("/api/scan-sources/seed");
      toast.success(res.message);
      refetchSources();
    } catch {
      toast.error("Failed to initialize sources");
    } finally {
      setSeeding(false);
    }
  };

  const usagePct = aiUsage
    ? (aiUsage.thisMonth.estimatedCost / aiUsage.budgetCeiling) * 100
    : 0;

  // Group sources by sourceType
  const grouped: Record<string, ScanSource[]> = {};
  for (const source of scanSources) {
    if (source.sourceType === "manual") continue;
    if (!grouped[source.sourceType]) grouped[source.sourceType] = [];
    grouped[source.sourceType]!.push(source);
  }

  const totalActive = scanSources.filter((s) => s.enabled).length;
  const totalSources = scanSources.length;

  return (
    <div className="animate-fade-in max-w-3xl mx-auto px-6 py-6">
      <h1 className="font-brand text-4xl tracking-wide mb-6">SETTINGS</h1>

      {/* Initialize Sources */}
      {scanSources.length === 0 && !sourcesLoading && (
        <div className="glass-card rounded-lg p-4 mb-6 border border-electric-blue/20">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-electric-blue" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">No intelligence sources configured</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Initialize default sources to start scanning
              </p>
            </div>
            <Button
              onClick={handleSeed}
              disabled={seeding}
              className="bg-electric-blue hover:bg-electric-blue/90 text-white font-semibold text-xs uppercase tracking-wider"
            >
              {seeding ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Database className="w-3.5 h-3.5 mr-1.5" />}
              Initialize Sources
            </Button>
          </div>
        </div>
      )}

      {/* Scan Sources — 3 Layers */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-brand text-xl tracking-wide">INTELLIGENCE SOURCES</h2>
          {scanSources.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSeed}
              disabled={seeding}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${seeding ? "animate-spin" : ""}`} />
              Re-seed
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          {totalActive}/{totalSources} sources active across 3 intelligence layers
        </p>

        {(["pain_scanner", "builder_intel", "trend_signal"] as const).map((layerKey) => {
          const layer = LAYER_CONFIG[layerKey];
          if (!layer) return null;
          const LayerIcon = layer.icon;
          const layerSources = grouped[layerKey] || [];
          const activeCount = layerSources.filter((s) => s.enabled).length;

          return (
            <div key={layerKey} className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <LayerIcon className={`w-4 h-4 ${layer.color}`} />
                <span className={`text-[11px] font-semibold uppercase tracking-wider ${layer.color}`}>
                  {layer.label}
                </span>
                <span className="text-[10px] text-muted-foreground ml-auto font-mono">
                  {activeCount}/{layerSources.length}
                </span>
              </div>
              <div className="space-y-1.5">
                {layerSources.map((source) => {
                  const isLocked = source.phase > 1;
                  const needsKey = source.requiresEnvKey && !source.enabled;

                  return (
                    <div
                      key={source.id}
                      className={`glass-card rounded-lg px-3 py-2 flex items-center gap-3 ${isLocked ? "opacity-50" : ""}`}
                    >
                      <button
                        onClick={() => !isLocked && toggleSource(source)}
                        disabled={isLocked}
                        className="shrink-0"
                      >
                        {isLocked ? (
                          <Lock className="w-5 h-5 text-[#444]" />
                        ) : source.enabled ? (
                          <ToggleRight className="w-5 h-5 text-mint" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#555]" />
                        )}
                      </button>
                      <button
                        className="flex-1 min-w-0 text-left hover:bg-[rgba(255,255,255,0.03)] rounded px-1 -mx-1 py-0.5 transition-colors group/name"
                        onClick={() => !isLocked && setEditingSource(source)}
                        disabled={isLocked}
                      >
                        <span
                          className={`text-sm font-mono block ${
                            source.enabled && !isLocked ? "text-foreground" : "text-[#555]"
                          }`}
                        >
                          {source.name}
                          {!isLocked && (
                            <Settings2 className="w-3 h-3 inline ml-1.5 opacity-30 group-hover/name:opacity-60 transition-opacity" />
                          )}
                        </span>
                        <span className="text-[10px] text-muted-foreground block truncate">
                          {source.description}
                        </span>
                      </button>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {source.lastRunStatus && (
                          <span className={`text-[9px] font-mono ${
                            source.lastRunStatus === "success" ? "text-mint" : "text-red-400"
                          }`}>
                            {source.lastRunCount ?? 0}
                          </span>
                        )}
                        {isLocked && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.05)] text-[#555]">
                            Phase {source.phase}
                          </span>
                        )}
                        {needsKey && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-electric-yellow/10 text-electric-yellow">
                            {source.requiresEnvKey?.includes("FIRECRAWL") ? (
                              <><Flame className="w-2.5 h-2.5 inline mr-0.5" />Firecrawl</>
                            ) : (
                              <><KeyRound className="w-2.5 h-2.5 inline mr-0.5" />API Key</>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {editingSource && (
          <SourceConfigDialog
            source={editingSource}
            onClose={() => setEditingSource(null)}
            onSave={async (config) => {
              try {
                await api.patch(`/api/scan-sources/${editingSource.id}`, { config });
                toast.success(`Updated ${editingSource.name} config`);
                refetchSources();
                setEditingSource(null);
              } catch {
                toast.error("Failed to save config");
              }
            }}
          />
        )}
      </section>

      {/* Account */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">ACCOUNT</h2>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input defaultValue={commander.email} className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1 max-w-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Change Password</Label>
            <Input type="password" placeholder="New password" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1 max-w-sm" />
          </div>
        </div>
      </section>

      {/* API Keys */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">API KEYS</h2>
        <div className="glass-card rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">ANTHROPIC API KEY</span>
              <p className="font-mono text-sm text-foreground mt-1">
                {showApiKey ? "sk-ant-api03-xxxxxxxxxxxx" : "sk-ant-api03-\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
              </p>
            </div>
            <button onClick={() => setShowApiKey(!showApiKey)} className="text-muted-foreground hover:text-foreground">
              {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </section>

      {/* Token Usage */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">AI TOKEN USAGE</h2>
        <div className="glass-card rounded-lg p-4">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">TODAY</span>
              <p className="font-mono text-lg text-foreground">
                {aiUsage ? aiUsage.today.totalTokens.toLocaleString() : "\u2014"}
              </p>
              <p className="text-[10px] text-muted-foreground font-mono">
                ${aiUsage ? aiUsage.today.estimatedCost.toFixed(4) : "0.00"}
              </p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">THIS WEEK</span>
              <p className="font-mono text-lg text-foreground">
                {aiUsage ? aiUsage.thisWeek.totalTokens.toLocaleString() : "\u2014"}
              </p>
              <p className="text-[10px] text-muted-foreground font-mono">
                ${aiUsage ? aiUsage.thisWeek.estimatedCost.toFixed(4) : "0.00"}
              </p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">THIS MONTH</span>
              <p className="font-mono text-lg text-foreground">
                {aiUsage ? aiUsage.thisMonth.totalTokens.toLocaleString() : "\u2014"}
              </p>
              <p className="text-[10px] text-muted-foreground font-mono">
                ${aiUsage ? aiUsage.thisMonth.estimatedCost.toFixed(4) : "0.00"}
              </p>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Monthly budget</span>
              <span className="font-mono">
                ${aiUsage ? aiUsage.thisMonth.estimatedCost.toFixed(2) : "0.00"} / ${aiUsage?.budgetCeiling ?? 20} ceiling
              </span>
            </div>
            <Progress value={Math.min(usagePct, 100)} className="h-2" />
            {usagePct > 80 && (
              <p className="text-[10px] text-electric-yellow mt-1 font-mono">
                Warning: Approaching budget ceiling
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Data Management */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">DATA MANAGEMENT</h2>
        <div className="space-y-3">
          <div className="glass-card rounded-lg p-4 flex items-center gap-3">
            <Archive className="w-5 h-5 text-electric-yellow shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Mark V1 Opportunities as Legacy</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Hides old V1 opportunities (raw signals like subreddit names) from the Big Board and Opportunities page. V2 synthesized opportunities will be shown instead.
              </p>
            </div>
            <Button
              onClick={async () => {
                setMarkingLegacy(true);
                try {
                  const res = await api.post<{ message: string; count: number }>("/api/opportunities/mark-legacy");
                  toast.success(res.message);
                } catch {
                  toast.error("Failed to mark legacy opportunities");
                } finally {
                  setMarkingLegacy(false);
                }
              }}
              disabled={markingLegacy}
              className="bg-electric-yellow/20 hover:bg-electric-yellow/30 text-electric-yellow border border-electric-yellow/30 font-semibold text-xs uppercase tracking-wider shrink-0"
            >
              {markingLegacy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Archive className="w-3.5 h-3.5 mr-1.5" />}
              Mark Legacy
            </Button>
          </div>
          <div className="glass-card rounded-lg p-4 flex items-center gap-3">
            <Trash2 className="w-5 h-5 text-red-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Clear Raw Signals</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Removes all raw signals from the database. Use this to reset the dedup cache and allow a fresh scan.
              </p>
            </div>
            <Button
              onClick={async () => {
                if (!confirm("Clear all raw signals? This will allow the next scan to re-fetch everything.")) return;
                setClearingSignals(true);
                try {
                  const res = await api.post<{ message: string; count: number }>("/api/raw-signals/clear");
                  toast.success(res.message);
                } catch {
                  toast.error("Failed to clear signals");
                } finally {
                  setClearingSignals(false);
                }
              }}
              disabled={clearingSignals}
              className="bg-red-400/20 hover:bg-red-400/30 text-red-400 border border-red-400/30 font-semibold text-xs uppercase tracking-wider shrink-0"
            >
              {clearingSignals ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Clear Signals
            </Button>
          </div>
        </div>
      </section>

      {/* Scan History */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">SCAN HISTORY</h2>
        {scanLogs.length > 0 ? (
          <div className="space-y-2">
            {scanLogs.map((log) => (
              <ScanLogRow key={log.id} log={log} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No scans yet. Deploy Strangelove from The Big Board to start scanning.</p>
        )}
      </section>
    </div>
  );
}

// ── Source Config Dialog ──

function TagList({ items, onRemove }: { items: string[]; onRemove: (i: number) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className="inline-flex items-center gap-1 bg-[rgba(255,255,255,0.08)] text-foreground text-xs font-mono px-2 py-0.5 rounded"
        >
          {item}
          <button onClick={() => onRemove(i)} className="hover:text-red-400 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

function AddItemInput({ placeholder, onAdd }: { placeholder: string; onAdd: (val: string) => void }) {
  const [val, setVal] = useState("");
  const submit = () => {
    const trimmed = val.trim();
    if (trimmed) { onAdd(trimmed); setVal(""); }
  };
  return (
    <div className="flex gap-2">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())}
        placeholder={placeholder}
        className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-sm flex-1"
      />
      <Button onClick={submit} size="sm" variant="outline" className="shrink-0">
        <Plus className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">{label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-sm w-20"
        min={1}
      />
    </div>
  );
}

function SourceConfigDialog({
  source,
  onClose,
  onSave,
}: {
  source: ScanSource;
  onClose: () => void;
  onSave: (config: Record<string, unknown>) => Promise<void>;
}) {
  const [config, setConfig] = useState<Record<string, unknown>>(() => structuredClone(source.config ?? {}));
  const [saving, setSaving] = useState(false);

  const platform = source.platform;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(config); } finally { setSaving(false); }
  };

  const renderEditor = () => {
    if (platform === "reddit_subreddit") {
      const subreddits = (config.subreddits ?? []) as string[];
      const limit = (config.limit ?? 10) as number;
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Subreddits</Label>
            <TagList items={subreddits} onRemove={(i) => setConfig({ ...config, subreddits: subreddits.filter((_, j) => j !== i) })} />
            <div className="mt-2">
              <AddItemInput placeholder="r/subreddit" onAdd={(v) => setConfig({ ...config, subreddits: [...subreddits, v.replace(/^r\//, "")] })} />
            </div>
          </div>
          <NumberField label="Posts per subreddit" value={limit} onChange={(n) => setConfig({ ...config, limit: n })} />
        </div>
      );
    }

    if (platform === "reddit_search") {
      const queries = (config.queries ?? []) as string[];
      const limit = (config.limit ?? 10) as number;
      const resultsPerKeyword = (config.results_per_keyword ?? 5) as number;
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Search Queries</Label>
            <TagList items={queries} onRemove={(i) => setConfig({ ...config, queries: queries.filter((_, j) => j !== i) })} />
            <div className="mt-2">
              <AddItemInput placeholder="search query" onAdd={(v) => setConfig({ ...config, queries: [...queries, v] })} />
            </div>
          </div>
          <div className="flex gap-4">
            <NumberField label="Results per keyword" value={resultsPerKeyword} onChange={(n) => setConfig({ ...config, results_per_keyword: n })} />
            <NumberField label="Limit" value={limit} onChange={(n) => setConfig({ ...config, limit: n })} />
          </div>
        </div>
      );
    }

    if (platform === "youtube") {
      const queries = (config.queries ?? []) as string[];
      const limit = (config.limit ?? 5) as number;
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Search Queries</Label>
            <TagList items={queries} onRemove={(i) => setConfig({ ...config, queries: queries.filter((_, j) => j !== i) })} />
            <div className="mt-2">
              <AddItemInput placeholder="search query" onAdd={(v) => setConfig({ ...config, queries: [...queries, v] })} />
            </div>
          </div>
          <NumberField label="Limit" value={limit} onChange={(n) => setConfig({ ...config, limit: n })} />
        </div>
      );
    }

    if (platform === "firecrawl") {
      const targets = (config.targets ?? []) as { name: string; url: string }[];
      const targetsPerScan = (config.targets_per_scan ?? 3) as number;
      return <FirecrawlEditor targets={targets} targetsPerScan={targetsPerScan} onChange={(t, n) => setConfig({ ...config, targets: t, targets_per_scan: n })} />;
    }

    // Fallback: read-only JSON
    return (
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Config (read-only)</Label>
        <pre className="text-xs font-mono text-muted-foreground bg-[rgba(255,255,255,0.03)] rounded p-3 overflow-auto max-h-60">
          {JSON.stringify(config, null, 2)}
        </pre>
      </div>
    );
  };

  const isReadOnly = !["reddit_subreddit", "reddit_search", "youtube", "firecrawl"].includes(platform);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[#0d0d0f] border-[rgba(255,255,255,0.08)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand tracking-wide text-lg">{source.name}</DialogTitle>
          <p className="text-xs text-muted-foreground">{source.description}</p>
        </DialogHeader>
        <div className="py-2">{renderEditor()}</div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          {!isReadOnly && (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-electric-blue hover:bg-electric-blue/90 text-white font-semibold text-xs uppercase tracking-wider"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FirecrawlEditor({
  targets,
  targetsPerScan,
  onChange,
}: {
  targets: { name: string; url: string }[];
  targetsPerScan: number;
  onChange: (targets: { name: string; url: string }[], targetsPerScan: number) => void;
}) {
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");

  const addTarget = () => {
    const name = newName.trim();
    const url = newUrl.trim();
    if (name && url) {
      onChange([...targets, { name, url }], targetsPerScan);
      setNewName("");
      setNewUrl("");
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Targets</Label>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {targets.map((t, i) => (
            <div key={i} className="flex items-center gap-2 bg-[rgba(255,255,255,0.04)] rounded px-2 py-1.5">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-foreground block truncate">{t.name}</span>
                <span className="text-[10px] text-muted-foreground block truncate">{t.url}</span>
              </div>
              <button
                onClick={() => onChange(targets.filter((_, j) => j !== i), targetsPerScan)}
                className="text-muted-foreground hover:text-red-400 transition-colors shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Target name"
            className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-sm flex-1"
          />
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTarget())}
            placeholder="https://..."
            className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-sm flex-1"
          />
          <Button onClick={addTarget} size="sm" variant="outline" className="shrink-0">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <NumberField label="Targets per scan" value={targetsPerScan} onChange={(n) => onChange(targets, n)} />
    </div>
  );
}

// ── Expandable scan log row ──

interface ScanSignalSummary {
  id: string;
  title: string;
  url: string | null;
  sourcePlatform: string;
  community: string | null;
  classification: string | null;
  industry: string | null;
  engagementScore: number;
  status: string;
}

interface SourceBreakdown {
  sourcePlatform: string;
  count: number;
}

function ScanLogRow({ log }: { log: ScanLog }) {
  const [expanded, setExpanded] = useState(false);
  const [signals, setSignals] = useState<ScanSignalSummary[] | null>(null);
  const [breakdown, setBreakdown] = useState<SourceBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSignals, setShowSignals] = useState(false);

  const sourcesScanned = Array.isArray(log.sourcesScanned) ? log.sourcesScanned as string[] : [];
  const details = (log.details || []) as ScanSourceDetail[];

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (signals !== null) return;

    setLoading(true);
    try {
      const res = await api.get<{ signals: ScanSignalSummary[]; breakdown: SourceBreakdown[] }>(`/api/scans/${log.id}/signals`);
      setSignals(res.signals);
      setBreakdown(res.breakdown);
    } catch {
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  const platformColors: Record<string, string> = {
    reddit_subreddit: "text-orange-400",
    reddit_search: "text-orange-400",
    hackernews: "text-amber-400",
    github: "text-purple-400",
    youtube: "text-red-400",
    rss: "text-blue-400",
    firecrawl: "text-yellow-400",
    subreddit_growth: "text-orange-300",
  };

  const platformLabels: Record<string, string> = {
    reddit_subreddit: "Reddit",
    reddit_search: "Reddit Search",
    hackernews: "Hacker News",
    github: "GitHub",
    youtube: "YouTube",
    rss: "RSS",
    firecrawl: "Firecrawl",
    subreddit_growth: "Subreddit Growth",
  };

  const statusColor = (s: string) => {
    switch (s) {
      case "success": return "text-mint";
      case "timeout": return "text-electric-yellow";
      case "error": return "text-red-400";
      case "skipped": return "text-[#555]";
      case "no_signals": return "text-[#777]";
      default: return "text-muted-foreground";
    }
  };

  const statusBg = (s: string) => {
    switch (s) {
      case "success": return "bg-mint/8";
      case "timeout": return "bg-electric-yellow/8";
      case "error": return "bg-red-400/8";
      case "skipped": return "bg-[rgba(255,255,255,0.03)]";
      case "no_signals": return "bg-[rgba(255,255,255,0.03)]";
      default: return "";
    }
  };

  // Compute warnings from details — identify the actual bottleneck
  const warnings: string[] = [];
  for (const d of details) {
    if (d.status === "timeout") {
      warnings.push(`${d.sourceName} timed out after ${Math.round(d.fetchDurationMs / 1000)}s`);
    } else if (d.status === "error") {
      warnings.push(`${d.sourceName}: ${d.errorMessage?.slice(0, 80) || "unknown error"}`);
    } else if (d.status === "success" && d.rawItemsFetched > 0 && d.signalsStored === 0) {
      // Figure out WHERE the funnel dropped to zero
      if (d.itemsAfterKeywordFilter === 0) {
        warnings.push(`${d.sourceName}: ${d.rawItemsFetched} fetched but 0 passed keyword filter`);
      } else if (d.itemsAfterDedup === 0) {
        warnings.push(`${d.sourceName}: ${d.itemsAfterKeywordFilter} passed filter but all already seen (dedup)`);
      } else {
        warnings.push(`${d.sourceName}: ${d.itemsAfterDedup} new signals but all classified as noise`);
      }
    }
    // Don't flag no_signals or skipped as issues — they're informational
  }

  return (
    <div className="glass-card rounded-lg overflow-hidden">
      <button
        onClick={handleToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          )}
          <div>
            <span className="text-sm text-foreground font-medium">{log.agentType.replace(/_/g, " ")}</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              {sourcesScanned.length} sources &middot; {log.opportunitiesFound} found &middot; {log.opportunitiesScored} scored
            </p>
          </div>
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          {warnings.length > 0 && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-electric-yellow/10 text-electric-yellow">
              {warnings.length} issue{warnings.length > 1 ? "s" : ""}
            </span>
          )}
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
            log.status === "completed" ? "bg-mint/10 text-mint" :
            log.status === "running" ? "bg-electric-blue/10 text-electric-blue" :
            "bg-red-400/10 text-red-400"
          }`}>
            {log.status}
          </span>
          {log.completedAt && (
            <p className="text-[10px] text-muted-foreground">
              {new Date(log.completedAt).toLocaleString()}
            </p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[rgba(255,255,255,0.06)] px-3 pb-3">

          {/* Per-source detail breakdown */}
          {details.length > 0 && (
            <div className="mt-3 mb-3">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Source Breakdown</span>
              <div className="mt-1.5 rounded-lg border border-[rgba(255,255,255,0.06)] overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[1fr_70px_55px_55px_55px_55px] gap-0 px-2.5 py-1.5 bg-[rgba(255,255,255,0.03)] text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">
                  <span>Source</span>
                  <span className="text-right">Status</span>
                  <span className="text-right">Fetched</span>
                  <span className="text-right">Filter</span>
                  <span className="text-right">New</span>
                  <span className="text-right">Stored</span>
                </div>
                {/* Rows */}
                {details.map((d) => (
                  <SourceDetailRow key={d.sourceName} detail={d} statusColor={statusColor} statusBg={statusBg} platformColors={platformColors} />
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="mb-3 p-2.5 rounded-lg bg-electric-yellow/5 border border-electric-yellow/10">
              <span className="text-[10px] text-electric-yellow uppercase tracking-wider font-semibold">
                {warnings.length} issue{warnings.length > 1 ? "s" : ""} detected
              </span>
              <ul className="mt-1.5 space-y-0.5">
                {warnings.map((w, i) => (
                  <li key={i} className="text-[11px] text-electric-yellow/80 font-mono">• {w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Legacy: Sources scanned (fallback when no details) */}
          {details.length === 0 && sourcesScanned.length > 0 && (
            <div className="mt-3 mb-3">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Sources Scanned</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {sourcesScanned.map((source) => (
                  <span key={source} className="text-[10px] bg-[rgba(255,255,255,0.06)] text-foreground px-2 py-0.5 rounded font-mono">
                    {source}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Source breakdown (from signals query) */}
          {breakdown.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Stored Signals by Platform</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {breakdown.map((b) => (
                  <span key={b.sourcePlatform} className={`text-[11px] font-mono ${platformColors[b.sourcePlatform] || "text-foreground"}`}>
                    {platformLabels[b.sourcePlatform] || b.sourcePlatform}: {b.count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Error log */}
          {log.errorLog && (
            <div className="mb-3 p-2 rounded bg-red-400/5 border border-red-400/10">
              <span className="text-[10px] text-red-400 uppercase tracking-wider font-semibold">Error</span>
              <p className="text-xs text-red-300 font-mono mt-1">{log.errorLog}</p>
            </div>
          )}

          {/* Signals toggle */}
          <button
            onClick={() => setShowSignals(!showSignals)}
            className="text-[10px] text-electric-blue hover:underline mb-2"
          >
            {showSignals ? "Hide" : "Show"} individual signals ({signals?.length ?? "..."})
          </button>

          {/* Signals list */}
          {showSignals && (
            loading ? (
              <div className="flex items-center gap-2 py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Loading signals...</span>
              </div>
            ) : signals && signals.length > 0 ? (
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                  Signals Found ({signals.length})
                </span>
                <div className="mt-1.5 space-y-1 max-h-[300px] overflow-y-auto">
                  {signals.map((signal) => (
                    <div
                      key={signal.id}
                      className="flex items-center gap-2 p-2 rounded bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-foreground truncate">{signal.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                          <span className={`font-mono ${platformColors[signal.sourcePlatform] || ""}`}>
                            {platformLabels[signal.sourcePlatform] || signal.sourcePlatform}
                          </span>
                          {signal.community && <span>{signal.community}</span>}
                          {signal.industry && (
                            <span className="bg-[rgba(255,255,255,0.06)] px-1 py-0.5 rounded">{signal.industry}</span>
                          )}
                          {signal.classification && (
                            <span className="uppercase">{signal.classification.replace("_", " ")}</span>
                          )}
                          <span className="font-mono">{signal.engagementScore} pts</span>
                        </div>
                      </div>
                      {signal.url && (
                        <a
                          href={signal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-electric-blue shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : signals && signals.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No signals were stored from this scan (all duplicates or filtered out).</p>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

// Per-source detail row within the scan log breakdown table
function SourceDetailRow({
  detail: d,
  statusColor,
  statusBg,
  platformColors,
}: {
  detail: ScanSourceDetail;
  statusColor: (s: string) => string;
  statusBg: (s: string) => string;
  platformColors: Record<string, string>;
}) {
  const [showDropped, setShowDropped] = useState(false);
  const hasFunnel = d.fetchAttempted && d.rawItemsFetched > 0;

  return (
    <div>
      <div
        className={`grid grid-cols-[1fr_70px_55px_55px_55px_55px] gap-0 px-2.5 py-1.5 border-t border-[rgba(255,255,255,0.04)] text-[11px] ${statusBg(d.status)}`}
      >
        <span className={`font-mono truncate ${platformColors[d.platform] || "text-foreground"}`}>
          {d.sourceName}
          {d.fetchDurationMs > 0 && (
            <span className="text-[9px] text-muted-foreground ml-1">
              {d.fetchDurationMs < 1000 ? `${d.fetchDurationMs}ms` : `${(d.fetchDurationMs / 1000).toFixed(1)}s`}
            </span>
          )}
        </span>
        <span className={`text-right font-mono uppercase text-[9px] ${statusColor(d.status)}`}>
          {d.status}
        </span>
        <span className="text-right font-mono text-foreground">{hasFunnel ? d.rawItemsFetched : "\u2014"}</span>
        <span className="text-right font-mono text-foreground">{hasFunnel ? d.itemsAfterKeywordFilter : "\u2014"}</span>
        <span className="text-right font-mono text-foreground">{hasFunnel ? d.itemsAfterDedup : "\u2014"}</span>
        <span className={`text-right font-mono font-semibold ${d.signalsStored > 0 ? "text-mint" : "text-muted-foreground"}`}>
          {d.signalsStored}
        </span>
      </div>
      {/* Error message */}
      {d.errorMessage && (
        <div className="px-2.5 py-1 text-[10px] text-red-400/80 font-mono bg-red-400/5">
          {d.errorMessage.slice(0, 120)}
        </div>
      )}
      {/* Dropped titles toggle */}
      {d.sampleDroppedTitles && d.sampleDroppedTitles.length > 0 && (
        <div className="px-2.5 pb-1">
          <button
            onClick={() => setShowDropped(!showDropped)}
            className="text-[9px] text-electric-blue/70 hover:text-electric-blue"
          >
            {showDropped ? "Hide" : "Show"} {d.keywordFilterDropped} filtered-out titles
          </button>
          {showDropped && (
            <div className="mt-0.5 space-y-0.5">
              {d.sampleDroppedTitles.map((title, i) => (
                <p key={i} className="text-[10px] text-muted-foreground font-mono truncate pl-2 border-l border-[rgba(255,255,255,0.06)]">
                  {title}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
